import Plausible from 'plausible-tracker';

/**
 * Plausible Analytics integration.
 *
 * - Cookie-free, no personal data, privacy-first.
 * - Domain: pocketledger-2pu.pages.dev (the Cloudflare Pages preview host).
 *   Override via VITE_PLAUSIBLE_DOMAIN if you self-host or use a different domain.
 * - All events are best-effort and silently no-op when analytics is disabled
 *   (e.g. dev mode, missing domain, ad-blocker).
 *
 * To self-host Plausible instead, set:
 *   VITE_PLAUSIBLE_DOMAIN=your-domain.tld
 *   VITE_PLAUSIBLE_HOST=https://plausible.your-domain.tld
 */

const domain = import.meta.env['VITE_PLAUSIBLE_DOMAIN'] ?? 'pocketledger-2pu.pages.dev';
const host = import.meta.env['VITE_PLAUSIBLE_HOST'] as string | undefined;

const shouldTrack =
  typeof window !== 'undefined' && Boolean(domain) && import.meta.env.PROD !== false;

const plausible = shouldTrack
  ? Plausible({
      domain,
      ...(host ? { host } : {}),
      hashMode: false,
      trackLocalhost: false,
    })
  : null;

export interface EventProps {
  [key: string]: string | number | boolean;
}

/**
 * Fire a custom event. No-op when analytics is disabled.
 *
 * Names use snake_case by Plausible convention (e.g. "tool_card_click").
 * Props are flat key/value pairs; pass only non-PII data.
 */
export function trackEvent(name: string, props?: EventProps): void {
  if (!plausible) return;
  try {
    plausible.trackEvent(name, { props: props ?? {} });
  } catch {
    // Swallow - analytics should never break the app.
  }
}

/**
 * Track a pageview. Useful when the SPA route changes but you want explicit
 * control. The auto pageview from plausible-tracker also runs once on init.
 */
export function trackPageview(url?: string): void {
  if (!plausible) return;
  try {
    if (typeof url === 'string' && url.length > 0) {
      plausible.trackPageview({ url });
    } else {
      plausible.trackPageview();
    }
  } catch {
    // noop
  }
}

export const ANALYTICS_DOMAIN = domain;
export const ANALYTICS_ENABLED = shouldTrack;